import { ActionIcon, Autocomplete, Badge, Box, Button, Card, CloseButton, CopyButton, Fieldset, Group, Loader, Paper, SegmentedControl, Select, Stack, Switch, Text, Textarea, TextInput, Title, Tooltip } from "@mantine/core";
import CodeMirror, { EditorView } from "@uiw/react-codemirror";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EDITOR_BACKGROUND, EDITOR_STYLE } from "../../common/editor-theme";
import { FactTable } from "../../common/fact-table";
import { useInitialHashState, useRegisterShareState } from "../../common/share-state";
import { UtilityTitle } from "../../common/utility-title";
import { IconCheck, IconCopy, IconPlayerPlay, IconPlayerStop, IconPlus, IconTrash } from "../../icons";
import { COMMAND_EXTENSIONS, COMMAND_SETUP } from "./editor";
import { addOption, addUrl, arrange, type Entry, type OptionEntry, optionNames, removeAt, setValue, type Slot } from "./entries";
import { addableOptions, findLong, flagsOf, type OptionSpec } from "./options";
import { parseCurl } from "./parse";
import { planRequest } from "./request";
import { SAMPLE_COMMAND } from "./sample";
import { explain, type Outcome, send } from "./send";
import { writeCurl } from "./write";

export default function Curl() {
  const initialState = useInitialHashState<{ command?: string; wrapped?: boolean }>();

  // Absent and empty differ, as they do for /json's document: a link that left the command out would put the sample
  // back over a box somebody had cleared, so the state always names it.
  const [initialCommand] = useState(() =>
    typeof initialState?.command === "string" ? initialState.command : SAMPLE_COMMAND
  );
  const [command, setCommand] = useState(initialCommand);
  const [wrapped, setWrapped] = useState(initialState?.wrapped ?? true);
  // The argument chosen but not yet added, which is the only state on this page the command text does not hold.
  const [pick, setPick] = useState<string | null>(null);
  // What a click on Send came to. A response is a moment rather than a setting, so none of it reaches the link.
  const [run, setRun] = useState<Run>(IDLE);
  const viewRef = useRef<EditorView | null>(null);
  const stopRef = useRef<AbortController | null>(null);

  useRegisterShareState(() => ({ command, wrapped }));

  // `window.curlEditor` is how devtools and the Playwright spec read the command; drop it on the way out.
  useEffect(() => () => {
    self.curlEditor = undefined;
    stopRef.current?.abort();
  }, []);

  const { entries, error, shell } = useMemo(() => parseCurl(command), [command]);
  const parts = useMemo(() => arrange(entries), [entries]);
  // Read on every render rather than at the click, so what the request will not honour is on screen before it is made.
  const plan = useMemo(() => (error ? null : planRequest(entries, self.location.protocol)), [entries, error]);
  const addable = useMemo(() => addableOptions(optionNames(entries)), [entries]);
  // A non-repeatable option written twice is two fields rather than one, the command holding both.
  const fields = useMemo(
    () => parts.singles.flatMap((block) => block.slots.map((slot) => ({ spec: block.spec, slot }))),
    [parts.singles],
  );

  // The editor is uncontrolled, as /json's and /csv's are, so the text is dispatched over the document rather than
  // handed back as a value — which is what keeps a caret in the editor from being thrown to the end on every render.
  const place = useCallback((text: string) => {
    setCommand(text);
    const editor = viewRef.current;
    if (!editor || editor.state.doc.toString() === text) return;
    editor.dispatch({ changes: { from: 0, to: editor.state.doc.length, insert: text } });
  }, []);

  // The command is the state and the builder is a view of it: every field above writes the whole command back, and
  // the page is then read out of that text again, so no field can ever disagree with what is on screen below it.
  const apply = useCallback((next: Entry[]) => place(writeCurl(next, wrapped, shell)), [place, wrapped, shell]);

  // The one edit that is not a change to the arguments, so it rewrites what is there rather than waiting for one:
  // a switch that appeared to do nothing until the next keystroke would be a switch nobody trusted.
  const handleLayout = (value: string) => {
    const next = value === WRAPPED;
    setWrapped(next);
    if (!error) place(writeCurl(entries, next, shell));
  };

  const handleAdd = () => {
    const spec = pick ? findLong(pick) : undefined;
    if (!spec) return;
    setPick(null);
    apply(addOption(entries, spec));
  };

  // Nothing gates the button on the command being sendable: a click is how the page says which part of it is not.
  const handleSend = useCallback(async () => {
    if (error || !plan) return setRun({ state: "failed", sent: null, message: error ?? "" });
    if (plan.error) return setRun({ state: "failed", sent: null, message: plan.error });

    // The line the card is headed with: the command is free to move on, and a response is of the request it answered.
    const sent = `${plan.method.toUpperCase()} ${plan.url}`;
    const controller = new AbortController();
    stopRef.current = controller;
    setRun({ state: "sending", sent });

    // The whole timeout is the command's where it sets one, and the abort behind Stop is always the other half.
    const signal = plan.timeout === null
      ? controller.signal
      : AbortSignal.any([controller.signal, AbortSignal.timeout(plan.timeout)]);

    try {
      setRun({ state: "answered", sent, outcome: await send(plan, signal) });
    } catch (thrown) {
      setRun({ state: "failed", sent, message: explain(thrown, plan, controller.signal.aborted) });
    } finally {
      if (stopRef.current === controller) stopRef.current = null;
    }
  }, [error, plan]);

  const handleStop = () => stopRef.current?.abort();

  const sending = run.state === "sending";

  return (
    <Stack gap="md">
      <UtilityTitle
        directory="curl"
        control={
          <SegmentedControl
            value={wrapped ? WRAPPED : ONE_LINE}
            onChange={handleLayout}
            aria-label="How the command is laid out"
            data={[{ value: WRAPPED, label: "Wrapped" }, { value: ONE_LINE, label: "One line" }]}
          />
        }
      >
        curl
      </UtilityTitle>

      {
        /* Nothing is drawn from a command that could not be read: an empty builder would read as an empty command,
        and one field written back out of it would take the text it could not parse with it. */
      }
      {error
        ? (
          <Text size="sm" c="dimmed">
            The builder shows the command once it reads as one. What is typed below is kept.
          </Text>
        )
        : (
          <Card withBorder shadow="sm" radius="md">
            <Stack gap="md">
              <Group justify="space-between" align="flex-end" wrap="nowrap">
                <Title order={4}>Arguments</Title>
                <Group gap="sm" align="flex-end" wrap="nowrap">
                  <Select
                    aria-label="Argument to add"
                    placeholder="Choose an argument"
                    data={addable}
                    value={pick}
                    onChange={setPick}
                    searchable
                    nothingFoundMessage="No argument by that name"
                    w={280}
                    comboboxProps={{ withinPortal: true }}
                  />
                  <Button
                    variant="light"
                    leftSection={<IconPlus size="0.9rem" />}
                    onClick={handleAdd}
                    disabled={pick === null}
                  >
                    Add
                  </Button>
                </Group>
              </Group>

              {/* curl takes as many URLs as it is given, so the operand is a fieldset like any other repeat. */}
              <Fieldset legend="URL">
                <Stack gap="xs">
                  {parts.urls.length === 0 && <Text size="sm" c="dimmed">This command has no URL yet.</Text>}
                  {parts.urls.map((slot, at) => (
                    <TextInput
                      key={slot.index}
                      aria-label={`URL ${at + 1}`}
                      placeholder="https://example.com/path"
                      value={slot.entry.value}
                      onChange={(event) => apply(setValue(entries, slot.index, event.currentTarget.value))}
                      spellCheck={false}
                      autoComplete="off"
                      autoCapitalize="off"
                      styles={MONOSPACE}
                      rightSectionPointerEvents="all"
                      rightSection={
                        <Remove
                          label={`Remove URL ${at + 1}`}
                          onClick={() => apply(removeAt(entries, slot.index))}
                        />
                      }
                    />
                  ))}
                  <Group>
                    <Button
                      size="xs"
                      variant="light"
                      leftSection={<IconPlus size="0.9rem" />}
                      onClick={() => apply(addUrl(entries))}
                    >
                      Add URL
                    </Button>
                  </Group>
                </Stack>
              </Fieldset>

              {/* An option curl reads only once is a field, two abreast where the region has the width for them. */}
              {chunk(fields).map((row) => (
                <Box key={`${row[0].spec.name}-${row[0].slot.index}`} className="settings-row">
                  {row.map(({ spec, slot }) => (
                    <ArgumentInput
                      key={slot.index}
                      spec={spec}
                      slot={slot}
                      label={spec.label}
                      onChange={(value) => apply(setValue(entries, slot.index, value))}
                      onRemove={() =>
                        apply(removeAt(entries, slot.index))}
                    />
                  ))}
                </Box>
              ))}

              {
                /* One curl reads every one of goes in a fieldset of its own, so the rows read as the one argument
                repeated rather than as a column of unrelated boxes. */
              }
              {parts.groups.map((block) => (
                <Fieldset key={block.spec.name} legend={`${block.spec.label} (${flagsOf(block.spec)})`}>
                  <Stack gap="xs">
                    {block.spec.hint && <Text size="xs" c="dimmed">{block.spec.hint}</Text>}
                    {block.slots.map((slot, at) => (
                      <ArgumentInput
                        key={slot.index}
                        spec={block.spec}
                        slot={slot}
                        position={at + 1}
                        onChange={(value) => apply(setValue(entries, slot.index, value))}
                        onRemove={() =>
                          apply(removeAt(entries, slot.index))}
                      />
                    ))}
                    <Group>
                      <Button
                        size="xs"
                        variant="light"
                        leftSection={<IconPlus size="0.9rem" />}
                        onClick={() =>
                          apply(addOption(entries, block.spec))}
                      >
                        Add {block.spec.label.toLowerCase()}
                      </Button>
                    </Group>
                  </Stack>
                </Fieldset>
              ))}

              {
                /* A flag has no value to show, so a row each would be a column of empty boxes; turning one off is what
                takes it off the command, there being nothing else a switch that is always on could mean. */
              }
              {parts.flags.length > 0 && (
                <Fieldset legend="Flags">
                  <Group gap="lg">
                    {parts.flags.flatMap((block) =>
                      block.slots.map((slot) => (
                        <Tooltip key={slot.index} label={flagsOf(block.spec)} withArrow>
                          <Switch
                            checked
                            label={block.spec.label}
                            onChange={() => apply(removeAt(entries, slot.index))}
                          />
                        </Tooltip>
                      ))
                    )}
                  </Group>
                </Fieldset>
              )}

              {parts.unknown.length > 0 && (
                <Fieldset legend="Not recognised">
                  <Stack gap="xs">
                    <Text size="xs" c="dimmed">
                      No field is drawn for these, and they are carried through the command exactly as written.
                    </Text>
                    <Group gap="xs">
                      {parts.unknown.map((slot) => (
                        <Badge
                          key={slot.index}
                          variant="light"
                          color="gray"
                          size="lg"
                          styles={BADGE_TEXT}
                          rightSection={
                            <CloseButton
                              size="xs"
                              variant="transparent"
                              aria-label={`Remove ${slot.entry.flag}`}
                              onClick={() => apply(removeAt(entries, slot.index))}
                            />
                          }
                        >
                          {slot.entry.flag}
                        </Badge>
                      ))}
                    </Group>
                  </Stack>
                </Fieldset>
              )}
            </Stack>
          </Card>
        )}

      <Card withBorder shadow="sm" radius="md">
        <Stack gap="xs">
          <Group justify="space-between" wrap="nowrap">
            <Title order={4}>Command</Title>
            <Group gap="xs" wrap="nowrap">
              <Button
                size="xs"
                onClick={handleSend}
                disabled={sending}
                leftSection={<IconPlayerPlay size="0.9rem" />}
              >
                Send
              </Button>
              <Button
                size="xs"
                variant="default"
                onClick={handleStop}
                disabled={!sending}
                leftSection={<IconPlayerStop size="0.9rem" />}
              >
                Stop
              </Button>
              <CopyButton value={command} timeout={2000}>
                {({ copied, copy }) => (
                  <Tooltip label={copied ? "Copied" : "Copy"} withArrow position="left">
                    <ActionIcon
                      color={copied ? "teal" : "gray"}
                      variant="subtle"
                      onClick={copy}
                      disabled={command === ""}
                      aria-label="Copy the command"
                    >
                      {copied ? <IconCheck size="1.2rem" /> : <IconCopy size="1.2rem" />}
                    </ActionIcon>
                  </Tooltip>
                )}
              </CopyButton>
            </Group>
          </Group>

          <Paper withBorder radius="sm" className="curl-command">
            <CodeMirror
              value={initialCommand}
              style={EDITOR_STYLE}
              minHeight="7rem"
              maxHeight="22rem"
              theme="dark"
              basicSetup={COMMAND_SETUP}
              extensions={COMMAND_EXTENSIONS}
              onCreateEditor={(editor) => {
                viewRef.current = editor;
                self.curlEditor = editor;
              }}
              onChange={setCommand}
            />
          </Paper>

          {error && <Text size="sm" c="red">{error}</Text>}

          {
            /* Said before the click and not after it: a POST is not a thing to explain once it has already gone. */
          }
          <Text size="xs" c="dimmed">
            Send makes the request from this tab, which is the one thing on this site that leaves your browser.
          </Text>

          {
            /* What the command asks for that a fetch has no way of doing, so nothing is quietly not done. */
          }
          {plan && plan.notes.length > 0 && (
            <Stack gap={2}>
              <Text size="xs" c="dimmed">The browser cannot do all of this:</Text>
              {plan.notes.map((note) => (
                <Text key={note.subject + note.reason} size="xs" c="dimmed">
                  <Text span ff="monospace" inherit>{note.subject}</Text> — {note.reason}
                </Text>
              ))}
            </Stack>
          )}
        </Stack>
      </Card>

      {run.state !== "idle" && <Response run={run} />}
    </Stack>
  );
}

// What came back, or why nothing did. It is drawn last, under the command rather than beside the builder, being about
// the text above it and not about the fields; and it is state this page holds and never shares, a response being a
// moment somebody had rather than a setting somebody chose.
function Response({ run }: { run: Run }) {
  const outcome = run.state === "answered" ? run.outcome : null;

  return (
    <Card withBorder shadow="sm" radius="md">
      <Stack gap="xs">
        <Group justify="space-between" align="center" wrap="nowrap">
          <Group gap="sm" align="center" wrap="nowrap">
            <Title order={4}>Response</Title>
            {outcome && !outcome.opaque && (
              <Badge variant="light" color={statusColour(outcome.status)} styles={BADGE_TEXT}>
                {outcome.status} {outcome.statusText}
              </Badge>
            )}
          </Group>

          {run.state === "sending"
            ? (
              <Group gap="xs" wrap="nowrap">
                <Loader size="xs" />
                <Text size="sm" c="dimmed">Sending…</Text>
              </Group>
            )
            : outcome && !outcome.opaque && (
              <Group gap="xs" wrap="nowrap">
                <Text size="sm" c="dimmed">{summary(outcome)}</Text>
                <CopyButton value={outcome.body} timeout={2000}>
                  {({ copied, copy }) => (
                    <Tooltip label={copied ? "Copied" : "Copy"} withArrow position="left">
                      <ActionIcon
                        color={copied ? "teal" : "gray"}
                        variant="subtle"
                        onClick={copy}
                        disabled={outcome.body === ""}
                        aria-label="Copy the body"
                      >
                        {copied ? <IconCheck size="1.2rem" /> : <IconCopy size="1.2rem" />}
                      </ActionIcon>
                    </Tooltip>
                  )}
                </CopyButton>
              </Group>
            )}
        </Group>

        {run.state !== "idle" && run.sent !== null && (
          <Text size="xs" c="dimmed" ff="monospace" style={{ overflowWrap: "anywhere" }}>{run.sent}</Text>
        )}

        {run.state === "failed" && <Text size="sm" c="red">{run.message}</Text>}

        {
          /* Status 0 and nothing else, which is all a filtered response carries — and what to turn on to get past it. */
        }
        {outcome?.opaque && (
          <Text size="sm" c="dimmed">
            The server answered with a redirect. curl stops on one too without -L, and a browser will not hand the page
            the answer it stopped on — turn Follow redirects on to go where it leads.
          </Text>
        )}

        {outcome && !outcome.opaque && (
          <>
            {outcome.headers.length > 0 && (
              <FactTable rows={outcome.headers.map(([name, value]) => ({ label: name, value }))} />
            )}
            <Text size="xs" c="dimmed">
              A browser hands the page only the headers the server marks as exposed, so this is usually shorter than
              what curl would print.
            </Text>

            <Paper
              withBorder
              radius="sm"
              style={{ backgroundColor: EDITOR_BACKGROUND, maxHeight: "26rem", overflow: "auto" }}
            >
              <Text
                component="pre"
                role="log"
                aria-label="Response body"
                ff="monospace"
                fz={EDITOR_STYLE.fontSize}
                p="sm"
                m={0}
                c={outcome.body === "" || outcome.binary ? "dimmed" : undefined}
                style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
              >
                {bodyText(outcome)}
              </Text>
            </Paper>
          </>
        )}
      </Stack>
    </Card>
  );
}

// One occurrence of one option: a box for its value, whatever shape that value is, and the button that takes it off.
function ArgumentInput({ spec, slot, label, position, onChange, onRemove }: ArgumentInputProps) {
  const name = position === undefined ? spec.label : `${spec.label} ${position}`;

  const shared = {
    label,
    description: label === undefined ? undefined : spec.hint,
    "aria-label": name,
    placeholder: spec.placeholder,
    value: slot.entry.value,
    spellCheck: false,
    autoComplete: "off",
    autoCapitalize: "off" as const,
    styles: MONOSPACE,
    rightSectionPointerEvents: "all" as const,
    rightSection: <Remove label={`Remove ${name.toLowerCase()}`} onClick={onRemove} />,
  };

  // A body runs to whatever length it runs to, so it grows with what is typed rather than scrolling in one line.
  if (spec.value === "body") {
    return (
      <Textarea
        {...shared}
        onChange={(event) => onChange(event.currentTarget.value)}
        autosize
        minRows={2}
        maxRows={10}
        // The section is centred on the box by default, which on a ten-line body is halfway down it.
        rightSectionProps={{ style: { alignItems: "flex-start", paddingTop: 6 } }}
      />
    );
  }

  // The listed values are the usual ones and never the only ones: a method curl will send is any word at all.
  if (spec.choices) {
    return <Autocomplete {...shared} data={spec.choices} onChange={onChange} />;
  }

  return <TextInput {...shared} onChange={(event) => onChange(event.currentTarget.value)} />;
}

function Remove({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Tooltip label="Remove" withArrow position="left">
      <ActionIcon variant="subtle" color="gray" onClick={onClick} aria-label={label}>
        <IconTrash size="1.1rem" />
      </ActionIcon>
    </Tooltip>
  );
}

interface ArgumentInputProps {
  spec: OptionSpec;
  slot: Slot<OptionEntry>;
  // Set where the field stands on its own and unset inside a fieldset, whose legend has already named the option.
  label?: string;
  position?: number;
  onChange: (value: string) => void;
  onRemove: () => void;
}

// A send has no "finished" of its own: what came back is the state, and a failure is one of the two things it can be.
// `sent` is what the request actually was, which the card needs because the command above it is free to move on.
type Run =
  | { state: "idle" }
  | { state: "sending"; sent: string }
  | { state: "answered"; sent: string; outcome: Outcome }
  | { state: "failed"; sent: string | null; message: string };

const IDLE: Run = { state: "idle" };

function statusColour(status: number): string {
  if (status < 300) return "teal";
  if (status < 400) return "blue";
  if (status < 500) return "orange";
  return "red";
}

function summary(outcome: Outcome): string {
  const bytes = `${outcome.bytes.toLocaleString()} ${outcome.bytes === 1 ? "byte" : "bytes"}`;
  return `${outcome.seconds.toFixed(2)}s · ${bytes}${outcome.redirected ? " · redirected" : ""}`;
}

// A body nobody can read as text is described rather than drawn, a panel of replacement characters saying nothing.
function bodyText(outcome: Outcome): string {
  if (outcome.binary) return "The response is not text, so it is not drawn here.";
  if (outcome.body === "") return "The response has no body.";
  return outcome.truncated ? `${outcome.body}\n\n… the rest is longer than this panel holds` : outcome.body;
}

const WRAPPED = "wrapped";
const ONE_LINE = "one-line";

const MONOSPACE = { input: { fontFamily: "monospace" } };

// A flag is a word to be read exactly, so it is set in the face the command above it is set in.
const BADGE_TEXT = { label: { fontFamily: "monospace", textTransform: "none" as const } };

// Two to a `.settings-row`, which is what gives them equal widths and stacks them once the region is narrow.
function chunk<T>(items: T[]): T[][] {
  const rows: T[][] = [];
  for (let at = 0; at < items.length; at += 2) rows.push(items.slice(at, at + 2));
  return rows;
}

declare global {
  var curlEditor: EditorView | undefined;
}
